import { useEffect, useState } from "react";
import { getUnixTime } from "date-fns";
import {
  DataRoomFolderQuery,
  useDataRoomFolderQuery,
  useUpdateDataRoomFolderMutation,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { ExclamationCircleIcon, FolderIcon } from "@heroicons/react/24/solid";
import { Dialog } from "@headlessui/react";
import { AnimatedModal } from "../../components/AnimatedModal";
import { toasts } from "../../components/toasts/toasts";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Button } from "@/src/components/tailwind/Button";
import { links } from "@/src/utils/links";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface DeleteFolderModalProps {
  onClose: () => void;
  open: boolean;
}

function DeleteFolderModal(props: DeleteFolderModalProps) {
  const id = useQuery().get("deleteFolder");

  return (
    <AnimatedModal onClose={props.onClose} open={props.open}>
      <DeleteFolderContent
        id={id}
        onCancel={() => props.onClose()}
        onDelete={() => props.onClose()}
      />
    </AnimatedModal>
  );
}

interface DeleteFolderContentProps {
  id: string | null;
  onCancel: () => void;
  onDelete: () => void;
}

function DeleteFolderContent(props: DeleteFolderContentProps) {
  const client = useGqlClient();
  const folderQuery = useDataRoomFolderQuery(
    client,
    {
      id: props.id || "",
    },
    {
      enabled: props.id !== null && props.id !== "",
    },
  );

  const [data, setData] = useState<DataRoomFolderQuery | null>(null);

  useEffect(() => {
    if (folderQuery.data) {
      setData(folderQuery.data);
    }
  }, [folderQuery.data]);

  if (folderQuery.isPending && !data) {
    return (
      <div>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <FolderIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              Loading...
            </Dialog.Title>
            <div className="mt-2">
              <p className="text-sm text-gray-500">Please wait</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (folderQuery.error || !data) {
    return (
      <div>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationCircleIcon
              className="h-6 w-6 text-red-600"
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              Something went wrong
            </Dialog.Title>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                We couldn't load the folder. Please try again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <FolderIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
        </div>
        <div className="text-center sm:ml-4 sm:mt-0 sm:text-left">
          <Dialog.Title
            as="h3"
            className="text-base font-semibold leading-6 text-gray-900"
          >
            {`Delete ${data.dataRoomFolder.name}`}?
          </Dialog.Title>
        </div>
      </div>

      <DeleteFolderContentBody
        data={data}
        onDelete={props.onDelete}
        onCancel={props.onCancel}
      />
    </div>
  );
}

function DeleteDdqlFolderContentBody(props: DeleteFolderContentBodyProps) {
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();
  return (
    <div className="mt-2">
      <p className="text-sm text-gray-500">
        You can't delete this folder as it is part of a question list. To
        delete, you need to delete the question list itself.
      </p>

      <div className="gap-x-2 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          variant="neutral"
          text="Go to question list"
          size="s"
          onClick={() => {
            history.push(
              links.ddqlLink(
                {
                  id: props.data.dataRoomFolder.ddql!.id,
                },
                dealId,
              ),
            );
          }}
        />
      </div>
    </div>
  );
}

function DeleteQuestionFolderContentBody(props: DeleteFolderContentBodyProps) {
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();
  return (
    <div className="mt-2">
      <p className="text-sm text-gray-500">
        You can't delete this folder as it is part of a question. To delete, you
        need to delete the question itself.
      </p>

      <div className="gap-x-2 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          variant="neutral"
          text="Go to question"
          size="s"
          onClick={() => {
            history.push(
              links.questionLink(
                {
                  id: props.data.dataRoomFolder.question!.id,
                },
                dealId,
              ),
            );
          }}
        />
      </div>
    </div>
  );
}

interface DeleteFolderContentBodyProps {
  data: DataRoomFolderQuery;
  onDelete: () => void;
  onCancel: () => void;
}

function DeleteFolderContentBody(props: DeleteFolderContentBodyProps) {
  const client = useGqlClient();
  const updateDataRoomFolder = useUpdateDataRoomFolderMutation(client);
  const { invalidateDataRoomFolder } = useInvalidateQueryKeys();

  const data = props.data;

  if (data.dataRoomFolder.ddql) {
    return <DeleteDdqlFolderContentBody {...props} />;
  }

  if (data.dataRoomFolder.question) {
    return <DeleteQuestionFolderContentBody {...props} />;
  }

  return (
    <div>
      <div className="mt-2">
        <p className="text-sm text-gray-500">
          Are you sure you want to delete the{" "}
          <span className="font-semibold">{data.dataRoomFolder.name}</span>{" "}
          folder? All documents and folders inside will be deleted.
        </p>
      </div>
      <div className="gap-x-2 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          variant="danger"
          text="Delete"
          loadingText="Deleting..."
          size="s"
          isLoading={updateDataRoomFolder.isPending}
          onClick={() => {
            updateDataRoomFolder.mutate(
              {
                input: {
                  id: data.dataRoomFolder.id,
                  deletedAt: getUnixTime(new Date()),
                },
              },
              {
                onSuccess: () => {
                  const parentFolderId =
                    data.dataRoomFolder.parentFolders.length > 1
                      ? data.dataRoomFolder.parentFolders[1].id
                      : data.dataRoomFolder.id;
                  invalidateDataRoomFolder(parentFolderId);
                  props.onDelete();
                  toasts.success(`${data.dataRoomFolder.name} deleted`);
                },
              },
            );
          }}
        />

        <Button
          variant="neutral"
          text="Cancel"
          size="s"
          onClick={() => {
            props.onCancel();
          }}
        />
      </div>
    </div>
  );
}

export default DeleteFolderModal;
